import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import api, { formatApiError } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { BarChart3, TrendingDown, Users, Lock } from "lucide-react";

const EVENT_LABELS = {
  signup: "Sign-ups",
  hubspot_connected: "HubSpot connected",
  mapping_confirmed: "Mapping confirmed",
  first_dashboard_view: "First dashboard view",
  test_briefing_sent: "Test briefing sent",
  trial_to_paid: "Trial → paid",
  cancellation: "Cancellations",
};

const ORDERED_EVENTS = [
  "signup",
  "hubspot_connected",
  "mapping_confirmed",
  "first_dashboard_view",
  "test_briefing_sent",
  "trial_to_paid",
  "cancellation",
];

export default function AdminPage() {
  const { user } = useAuth();
  const nav = useNavigate();
  const [data, setData] = useState(null);
  const [signups, setSignups] = useState([]);
  const [err, setErr] = useState("");
  const [days, setDays] = useState(30);
  const [authorized, setAuthorized] = useState(null); // null=checking, true/false

  useEffect(() => {
    if (!user || user === false) return;
    api
      .get("/admin/whoami")
      .then(({ data }) => setAuthorized(!!data.is_admin))
      .catch(() => setAuthorized(false));
  }, [user]);

  useEffect(() => {
    if (!authorized) return;
    setErr("");
    api
      .get(`/admin/analytics?days=${days}`)
      .then(({ data }) => setData(data))
      .catch((e) => setErr(formatApiError(e.response?.data?.detail) || e.message));
    api
      .get(`/admin/signups?limit=50`)
      .then(({ data }) => setSignups(data.rows || []))
      .catch(() => setSignups([]));
  }, [authorized, days]);

  if (user === null) {
    return <div className="min-h-screen flex items-center justify-center text-sm text-slate-500">Loading…</div>;
  }
  if (user === false) {
    nav("/login");
    return null;
  }
  if (authorized === null) {
    return <div className="min-h-screen flex items-center justify-center text-sm text-slate-500" data-testid="admin-loading">Checking access…</div>;
  }
  if (authorized === false) {
    return (
      <div className="min-h-screen w-full bg-slate-50 flex items-center justify-center px-6" data-testid="admin-forbidden">
        <div className="max-w-md w-full rounded-2xl border border-slate-200 bg-white p-8 text-center">
          <div className="mx-auto h-12 w-12 rounded-full bg-slate-100 flex items-center justify-center">
            <Lock className="h-5 w-5 text-slate-500" />
          </div>
          <h1 className="mt-4 text-xl font-bold text-slate-900">Admin access required</h1>
          <p className="mt-2 text-sm text-slate-500">
            This page is only visible to accounts listed in <code className="text-slate-700 bg-slate-100 px-1 rounded">ADMIN_EMAILS</code>.
          </p>
          <button
            onClick={() => nav("/dashboard")}
            className="mt-6 rounded-lg bg-slate-900 text-white px-4 py-2 text-sm font-semibold hover:bg-slate-800"
          >
            Back to dashboard
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen w-full bg-slate-50 text-slate-900" data-testid="admin-page">
      <header className="border-b border-slate-200 bg-white">
        <div className="max-w-6xl mx-auto px-6 lg:px-10 py-4 flex items-center justify-between">
          <div className="flex items-center gap-2">
            <div className="h-8 w-8 rounded-lg bg-slate-900 text-white flex items-center justify-center">
              <BarChart3 className="h-4 w-4" />
            </div>
            <div>
              <div className="text-sm font-bold tracking-tight">Admin · Funnel analytics</div>
              <div className="text-[10px] uppercase tracking-[0.18em] text-slate-400">Internal</div>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <label className="text-xs text-slate-500">Window</label>
            <select
              data-testid="admin-days-select"
              value={days}
              onChange={(e) => setDays(Number(e.target.value))}
              className="rounded-lg border border-slate-300 bg-white text-sm px-2 py-1"
            >
              <option value="7">Last 7 days</option>
              <option value="30">Last 30 days</option>
              <option value="90">Last 90 days</option>
            </select>
            <button
              onClick={() => nav("/dashboard")}
              className="text-xs text-slate-500 hover:text-slate-900 ml-3"
            >
              Back to app →
            </button>
          </div>
        </div>
      </header>

      <main className="max-w-6xl mx-auto px-6 lg:px-10 py-8 space-y-8">
        {err && (
          <div className="rounded-lg border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-800" data-testid="admin-error">
            {err}
          </div>
        )}

        {!data ? (
          <div className="text-sm text-slate-500">Loading analytics…</div>
        ) : (
          <>
            {/* Totals */}
            <section data-testid="admin-totals">
              <div className="text-[11px] uppercase tracking-[0.22em] text-slate-500 font-semibold">
                Totals · last {data.days} days (since {data.since})
              </div>
              <div className="mt-4 grid grid-cols-2 md:grid-cols-4 gap-4">
                {ORDERED_EVENTS.map((ev) => (
                  <Stat
                    key={ev}
                    label={EVENT_LABELS[ev]}
                    value={data.totals?.[ev] || 0}
                    unique={data.unique_accounts_by_event?.[ev] || 0}
                    tone={ev === "cancellation" ? "danger" : "neutral"}
                  />
                ))}
              </div>
            </section>

            {/* Funnel conversions */}
            <section data-testid="admin-conversions">
              <div className="text-[11px] uppercase tracking-[0.22em] text-slate-500 font-semibold">
                Conversions between steps · unique accounts
              </div>
              <div className="mt-4 rounded-2xl border border-slate-200 bg-white overflow-hidden">
                <table className="w-full text-sm">
                  <thead className="bg-slate-50 text-slate-500 text-xs uppercase tracking-wider">
                    <tr>
                      <th className="text-left px-4 py-2 font-semibold">From</th>
                      <th className="text-left px-4 py-2 font-semibold">To</th>
                      <th className="text-right px-4 py-2 font-semibold">From count</th>
                      <th className="text-right px-4 py-2 font-semibold">To count</th>
                      <th className="text-right px-4 py-2 font-semibold">Conversion</th>
                    </tr>
                  </thead>
                  <tbody>
                    {data.conversions?.map((c, i) => (
                      <tr key={i} className="border-t border-slate-100" data-testid={`admin-conversion-${c.from}-to-${c.to}`}>
                        <td className="px-4 py-3 text-slate-700">{EVENT_LABELS[c.from] || c.from}</td>
                        <td className="px-4 py-3 text-slate-900 font-medium">{EVENT_LABELS[c.to] || c.to}</td>
                        <td className="px-4 py-3 text-right number-tabular">{c.from_count}</td>
                        <td className="px-4 py-3 text-right number-tabular">{c.to_count}</td>
                        <td className="px-4 py-3 text-right font-semibold number-tabular text-slate-900">
                          {c.pct.toFixed(1)}%
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </section>

            {/* Daily table */}
            <section data-testid="admin-daily">
              <div className="text-[11px] uppercase tracking-[0.22em] text-slate-500 font-semibold">
                Daily counts
              </div>
              <div className="mt-4 rounded-2xl border border-slate-200 bg-white overflow-x-auto">
                <table className="w-full text-xs">
                  <thead className="bg-slate-50 text-slate-500 uppercase tracking-wider">
                    <tr>
                      <th className="text-left px-3 py-2 font-semibold whitespace-nowrap">Date</th>
                      {ORDERED_EVENTS.map((ev) => (
                        <th key={ev} className="text-right px-3 py-2 font-semibold whitespace-nowrap">
                          {EVENT_LABELS[ev]}
                        </th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {[...(data.daily || [])].reverse().map((row) => (
                      <tr key={row.date} className="border-t border-slate-100">
                        <td className="px-3 py-2 text-slate-700 font-mono">{row.date}</td>
                        {ORDERED_EVENTS.map((ev) => (
                          <td key={ev} className="px-3 py-2 text-right number-tabular text-slate-900">
                            {row[ev] || 0}
                          </td>
                        ))}
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </section>

            {/* Landing-page visitors */}
            <section className="rounded-2xl border border-slate-200 bg-white p-6" data-testid="admin-landing-views">
              <div className="flex items-baseline justify-between mb-4">
                <h2 className="text-sm font-bold text-slate-900 uppercase tracking-[0.14em]">Landing-page visitors</h2>
                <div className="text-xs text-slate-500">Last {days} days · aggregate only, no PII</div>
              </div>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <div className="rounded-lg bg-slate-50 border border-slate-200 p-4">
                  <div className="text-[10px] uppercase tracking-[0.18em] text-slate-500 font-semibold">Total views</div>
                  <div className="mt-2 text-3xl font-extrabold text-slate-900">{data.landing_views || 0}</div>
                </div>
                <div className="md:col-span-2 rounded-lg bg-slate-50 border border-slate-200 p-4">
                  <div className="text-[10px] uppercase tracking-[0.18em] text-slate-500 font-semibold mb-3">Top referrers</div>
                  {(data.landing_sources || []).length === 0 ? (
                    <div className="text-sm text-slate-500">No traffic yet.</div>
                  ) : (
                    <ul className="space-y-1.5">
                      {(data.landing_sources || []).map((r) => (
                        <li key={r.source} className="flex items-center justify-between text-sm">
                          <span className="font-mono text-slate-700 truncate mr-3">{r.source}</span>
                          <span className="font-semibold text-slate-900 number-tabular">{r.count}</span>
                        </li>
                      ))}
                    </ul>
                  )}
                </div>
              </div>
            </section>

            {/* Recent signups */}
            <section className="rounded-2xl border border-slate-200 bg-white p-6" data-testid="admin-signups-table">
              <div className="flex items-baseline justify-between mb-4">
                <h2 className="text-sm font-bold text-slate-900 uppercase tracking-[0.14em]">Recent signups</h2>
                <div className="text-xs text-slate-500">Last {signups.length} · newest first</div>
              </div>
              {signups.length === 0 ? (
                <div className="text-sm text-slate-500">No signups yet.</div>
              ) : (
                <div className="overflow-x-auto">
                  <table className="w-full text-sm">
                    <thead>
                      <tr className="text-[10px] uppercase tracking-[0.14em] text-slate-500 font-semibold">
                        <th className="px-3 py-2 text-left">Email</th>
                        <th className="px-3 py-2 text-left">Name</th>
                        <th className="px-3 py-2 text-left">Signed up</th>
                        <th className="px-3 py-2 text-center">Verified</th>
                        <th className="px-3 py-2 text-center">HubSpot</th>
                        <th className="px-3 py-2 text-left">Plan</th>
                      </tr>
                    </thead>
                    <tbody>
                      {signups.map((s) => (
                        <tr key={s.id} className="border-t border-slate-100" data-testid={`admin-signup-row-${s.id}`}>
                          <td className="px-3 py-2 font-mono text-slate-900">{s.email}</td>
                          <td className="px-3 py-2 text-slate-700">{s.name || "—"}</td>
                          <td className="px-3 py-2 font-mono text-xs text-slate-500">{(s.created_at || "").slice(0, 16).replace("T", " ")}</td>
                          <td className="px-3 py-2 text-center">
                            {s.email_verified ? <span className="text-emerald-600">✓</span> : <span className="text-slate-300">—</span>}
                          </td>
                          <td className="px-3 py-2 text-center">
                            {s.hubspot_connected ? <span className="text-emerald-600">✓</span> : <span className="text-slate-300">—</span>}
                          </td>
                          <td className="px-3 py-2 text-slate-700 font-mono text-xs">{s.plan}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </section>
          </>
        )}
      </main>
    </div>
  );
}

function Stat({ label, value, unique, tone }) {
  const isDanger = tone === "danger";
  return (
    <div className="rounded-2xl border border-slate-200 bg-white p-4" data-testid="admin-stat-card">
      <div className={`text-[10px] uppercase tracking-[0.18em] font-semibold ${isDanger ? "text-red-500" : "text-slate-500"}`}>
        {label}
        {isDanger && <TrendingDown className="inline h-3 w-3 ml-1" />}
      </div>
      <div className={`mt-2 text-3xl font-extrabold ${isDanger ? "text-red-700" : "text-slate-900"}`}>{value}</div>
      <div className="mt-1 text-xs text-slate-500 inline-flex items-center gap-1">
        <Users className="h-3 w-3" /> {unique} unique account{unique === 1 ? "" : "s"}
      </div>
    </div>
  );
}
